import { useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import type { SessionResponse } from "@amber/access/contracts";
import { sessionQuery } from "@amber/access/ui";
import { Button, Callout, Skeleton, isApiError, useAppNavigate } from "@amber/ui";
import { AppShell } from "./AppShell";

export function AppFrame({
  view,
  status,
  topbar,
  children,
}: {
  view: string;
  status?: ReactNode;
  topbar?: ReactNode;
  children: (session: SessionResponse) => ReactNode;
}) {
  const session = useQuery(sessionQuery);
  const navigate = useAppNavigate();
  const href = useRouterState({ select: (s) => s.location.href });
  const signedOut = isApiError(session.error) && session.error.status === 401;

  useEffect(() => {
    if (signedOut && !href.startsWith("/sign-in")) navigate(`/sign-in?return_to=${encodeURIComponent(href)}`);
  }, [signedOut, href, navigate]);

  if (session.data) {
    return (
      <AppShell session={session.data} view={view} status={status} topbar={topbar}>
        {children(session.data)}
      </AppShell>
    );
  }
  if (session.isError && !signedOut) {
    return (
      <div className="mx-auto mt-24 max-w-[460px] px-4">
        <Callout
          tone="attention"
          role="alert"
          title="Couldn't load your session"
          actions={
            <Button
              size="sm"
              onClick={() => {
                void session.refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          {session.error.message}
        </Callout>
      </div>
    );
  }
  return (
    <div aria-busy="true" className="flex h-full flex-col">
      <div className="flex h-topbar items-center gap-3 border-b border-divider px-4">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="ml-6 h-4 w-72" />
      </div>
      <div className="flex-1" />
    </div>
  );
}
